import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { environment } from '../../config/environment';
import './JobListings.css';

// ===== ANIMATION VARIANTS =====
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay: i * 0.08 },
  }),
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const FloatingParticles = () => {
  const particles = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 5 + 2,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * 5,
      drift: Math.random() * 50 - 25,
    })), []
  );

  return (
    <div className="jl-particles">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="jl-particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            y: [0, -50, 0],
            x: [0, p.drift, 0],
            opacity: [0, 0.5, 0],
            scale: [0.3, 1.3, 0.3],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

const JobListings = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [workMode, setWorkMode] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobOpenings, setJobOpenings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${environment.baseUrl}${environment.endpoints.jobOpeningsGetCurrentOpening}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await response.json();

        if (!response.ok || data?.response !== true) {
          throw new Error(data?.message || 'Failed to fetch jobs.');
        }

        const jobs = Array.isArray(data?.data) 
          ? data.data.map((item) => {
              // Map job_type to proper display
              let workModeType = 'Full Time';
              const jobTypeStr = (item.job_type || '').toUpperCase().trim();
              
              if (jobTypeStr === 'ON SITE' || jobTypeStr === 'ONSITE' || jobTypeStr === 'ON-SITE') {
                workModeType = 'On Site';
              } else if (jobTypeStr === 'WORK FROM HOME' || jobTypeStr === 'REMOTE' || jobTypeStr === 'WFH') {
                workModeType = 'Remote';
              } else if (jobTypeStr === 'HYBRID') {
                workModeType = 'Hybrid';
              }

              return {
                id: item.id,
                title: item.title || 'Untitled Position',
                location: item.location || 'Patna, Bihar',
                type: workModeType,
                experience: item.experience ? `${item.experience} Years` : '0-2 Years',
                vacancies: item.vacancies || 0,
                education: item.education || 'Not Specified',
                description: item.description || 'Join our team and grow your career with us.',
                status: 'Active', // Default since API doesn't send status
              };
            })
          : [];

        // All jobs from API are assumed active
        setJobOpenings(jobs);

      } catch (err) {
        setError(err.message || 'Unable to load job openings.');
        setJobOpenings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const filteredJobs = useMemo(() => {
    const result = jobOpenings.filter(job => {
      let matchesWorkMode = true;
      const jobType = (job.type || '').toLowerCase().trim();
      
      if (workMode === 'onsite') {
        matchesWorkMode = jobType === 'on site' || 
                          jobType === 'onsite' || 
                          jobType.includes('on site') || 
                          jobType.includes('onsite');
      } else if (workMode === 'remote') {
        matchesWorkMode = jobType === 'remote' || 
                          jobType === 'work from home' || 
                          jobType === 'wfh' ||
                          jobType.includes('remote') ||
                          jobType.includes('home');
      }
      
      const matchesDepartment = activeFilter === 'all'; // Simplified since API doesn't give department
      const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           job.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesWorkMode && matchesDepartment && matchesSearch;
    });

    return result;

  }, [jobOpenings, activeFilter, workMode, searchTerm]);

  const handleApplyNow = (job) => {
    navigate('/job-details', { state: { job } });
  };

  

  const handleBackToHome = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="job-listings-page">
        <Header />
        <div className="jl-loading-container">
          <FloatingParticles />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="jl-loading-content"
          >
            <div className="jl-loader-ring">
              <div className="jl-loader-ring-inner" />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="jl-loading-text"
            >
              Loading<span className="jl-dot-1">.</span><span className="jl-dot-2">.</span><span className="jl-dot-3">.</span>
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="jl-loading-sub"
            >
              Finding the best opportunities for you
            </motion.p>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-listings-page">
        <Header />
        <div className="jl-error-container">
          <FloatingParticles />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="jl-error-content"
          >
            <motion.div
              animate={{ rotate: [0, -10, 10, -5, 0], scale: [1, 1.1, 1.1, 1] }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="jl-error-icon"
            >
              ⚠️
            </motion.div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="jl-retry-btn"
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-listings-page">
      <Header />
      
      <main className="job-listings-main">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          className="job-listings-topbar"
        >
          <div className="job-listings-topbar-left">
            <button className="job-listings-back-btn" onClick={handleBackToHome}>
              <svg className="job-listings-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back to Home
            </button>
            <span className="job-listings-breadcrumb">
              <span className="job-listings-breadcrumb-sep">/</span>
              <span className="job-listings-breadcrumb-current">Current Openings</span>
            </span>
          </div>
          <div className="job-listings-topbar-right">
            <span className="job-listings-job-count">
              {filteredJobs.length} {filteredJobs.length === 1 ? 'Opening' : 'Openings'}
            </span>
          </div>
        </motion.div>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
          className="job-listings-header"
        >
          <h1>
            Current <span className="jl-heading-accent">Openings</span>
          </h1>
          <p>
            Find the perfect role that matches your skills and experience.
          </p>
          <div className="jl-header-underline" />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="job-listings-filters"
        >
          <div className="job-listings-search">
            <input
              type="text"
              placeholder="Search jobs by title or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="job-listings-search-input"
            />
            <span className="job-listings-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
          </div>

          <div className="jl-workmode-buttons">
            {['all', 'onsite', 'remote'].map((mode) => (
              <button
                key={mode}
                className={`jl-workmode-btn ${workMode === mode ? 'active' : ''}`}
                onClick={() => setWorkMode(mode)}
              >
                {mode === 'all' ? 'All' : mode === 'onsite' ? 'On Site' : 'Remote'}
              </button>
            ))}
          </div>
        </motion.section>

        <section className="job-listings-cards">
          {filteredJobs.length > 0 ? (
            <motion.div
              className="job-listings-grid"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  variants={{
                    hidden: { opacity: 0, y: 30, scale: 0.95 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.6,
                        ease: [0.25, 0.46, 0.45, 0.94],
                        delay: index * 0.08,
                      },
                    },
                  }}
                  whileHover={{
                    y: -6,
                    boxShadow: '0 16px 48px rgba(29,78,216,0.15)',
                    borderColor: 'rgba(59,130,246,0.25)',
                    transition: { type: 'spring', stiffness: 300 },
                  }}
                  className="job-listing-card"
                >
                  <div className="job-listing-card-header">
                    <h3>{job.title}</h3>
                    <span className="job-listing-type">{job.type}</span>
                  </div>
                  
                  <div className="job-listing-card-details">
                    <span className="jl-detail-item">
                      <svg className="jl-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {job.location}
                    </span>
                    <span className="jl-detail-item">
                      <svg className="jl-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      {job.experience} Exp.
                    </span>
                    <span className="jl-detail-item">
                      <svg className="jl-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                      {job.vacancies} Openings
                    </span>
                    <span className="jl-detail-item">
                      <svg className="jl-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                      </svg>
                      {job.education}
                    </span>
                  </div>

                  <p className="job-listing-card-desc">
                    {job.description}
                  </p>

                  <div className="job-listing-card-footer">
                    <button
                      className="job-listing-apply-btn"
                      onClick={() => handleApplyNow(job)}
                    >
                      Apply Now
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft: '8px'}}>
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="job-listings-no-jobs">
              <span className="no-jobs-icon">📂</span>
              <h3>No active openings found</h3>
              <p>Check back later for new opportunities.</p>
            </div>
          )}
        </section>
      </main>

    </div>
  );
};

export default JobListings;